import { useContext, useState } from 'react';
import type { ParentStudyViewModel } from '../../../Semester/ParentStudyViewModel';
import type { IOnlineApplicationChoicesHandlers } from '../../Context/OnlineStudyPlanContext';
import { OnlineStudyPlanContext } from '../../Context/OnlineStudyPlanContext';
import {
    GetCreditsLabel,
    GetSubjectsTotalPoints,
    GetSubjectsTotalPrice,
    IsChoiceAddedToBasket,
} from '../../Context/OnlineStudyPlanHelpers';
import type { OnlineSemesterItemViewModel } from '../SubjectsBySemester';
import type { OnlineSemesterViewModel } from './Semester';
import { useSemesterHelper } from './SemesterHelpers';

export const useSemesterModel = (
    initialSemester: OnlineSemesterViewModel,
    initialItems: OnlineSemesterItemViewModel[],
    parentStudy: ParentStudyViewModel
) => {
    const context = useContext(OnlineStudyPlanContext);
    const [items, setItems] = useState(initialItems);

    const setItemState = (
        item: OnlineSemesterItemViewModel,
        itemSetter: (prevItem: OnlineSemesterItemViewModel) => void
    ) => {
        setItems(prevState => {
            const currentItems = [...prevState];
            itemSetter(currentItems[item.index]);

            return currentItems;
        });
    };
    const semesterHelper = useSemesterHelper(
        initialSemester,
        parentStudy,
        setItemState,
        context as IOnlineApplicationChoicesHandlers
    );
    const hasSemesterUnusedElectivePoints = () =>
        items.filter(i => !i.obligatorySubject).some(i => semesterHelper.getHasItemUnusedElectivePoints(i));

    const addSemesterToBasket = () => {
        items.forEach(i => {
            if (i.obligatorySubject) {
                semesterHelper.addObligatoryItemToBasket(i);
            } else {
                semesterHelper.addElectiveItemToBasket(i);
            }
        });
    };

    const removeSemesterFromBasket = () => {
        items.forEach(i => {
            if (i.obligatorySubject) {
                semesterHelper.removeObligatoryItemFromBasket(i);
            } else {
                semesterHelper.removeElectiveItemFromBasket(i);
            }
        });
    };

    const isSemesterAddedToBasket = () => {
        const allChoicesFromSemester = semesterHelper.getAllChoicesFromSemester(items);
        const { choicesFromBasket } = context;

        return (
            hasSemesterUnusedElectivePoints() === false &&
            allChoicesFromSemester.every(choice => IsChoiceAddedToBasket(choicesFromBasket, choice))
        );
    };

    const getTotalPriceOfSemester = () => {
        const choices = semesterHelper.getAllChoicesFromSemester(items);

        return GetSubjectsTotalPrice(choices.map(c => c.subject));
    };

    const getTotalPointsOfSemester = () => {
        const choices = semesterHelper.getAllChoicesFromSemester(items);

        return GetSubjectsTotalPoints(choices.map(c => c.subject));
    };

    const getCreditsLabel = () => {
        const choices = semesterHelper.getAllChoicesFromSemester(items);

        return GetCreditsLabel(choices.map(c => c.subject));
    };

    return {
        hasSemesterUnusedElectivePoints,
        isSemesterAddedToBasket,
        addSemesterToBasket,
        removeSemesterFromBasket,
        items,
        setItemState,
        getTotalPriceOfSemester,
        getTotalPointsOfSemester,
        getCreditsLabel,
    };
};
